import { useEffect, useRef } from "react";

const COLORS = [
  "#fcbd00",
  "#ff570a",
  "#32ab5e",
  "#ffffff",
  "#ca3333",
  "#fecf08",
];

interface ConfettiParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  g: number;
  rot: number;
  vr: number;
  s: number;
  c: string;
  life: number;
}

export const useRaffleConfetti = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const particlesRef = useRef<ConfettiParticle[]>([]);
  const timeoutRefs = useRef<number[]>([]);

  const burst = (x: number, y: number, count: number) => {
    for (let index = 0; index < count; index += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 3 + Math.random() * 9;

      particlesRef.current.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 4,
        g: 0.22 + Math.random() * 0.12,
        rot: Math.random() * 6,
        vr: 0.2 - Math.random() * 0.4,
        s: 5 + Math.random() * 7,
        c: COLORS[(Math.random() * COLORS.length) | 0],
        life: 60 + Math.random() * 40,
      });
    }
  };

  const celebrateWinner = ({ x, y }: { x: number; y: number }) => {
    const width = canvasRef.current?.clientWidth ?? window.innerWidth;
    const height = canvasRef.current?.clientHeight ?? window.innerHeight;

    burst(x, y, 66);

    const timeoutId = window.setTimeout(() => {
      burst(width * 0.24, height * 0.34, 36);
      burst(width * 0.76, height * 0.34, 36);
    }, 70);

    timeoutRefs.current.push(timeoutId);
  };

  const celebrateFinale = () => {
    const width = canvasRef.current?.clientWidth ?? window.innerWidth;
    const height = canvasRef.current?.clientHeight ?? window.innerHeight;

    for (let index = 0; index < 5; index += 1) {
      const timeoutId = window.setTimeout(() => {
        burst(
          Math.random() * width,
          height * (0.2 + Math.random() * 0.4),
          48
        );
        burst(
          Math.random() * width,
          height * (0.2 + Math.random() * 0.4),
          48
        );
      }, index * 150);

      timeoutRefs.current.push(timeoutId);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");

    if (!canvas || !context) return undefined;

    const sizeCanvas = () => {
      const ratio = window.devicePixelRatio || 1;
      const { width, height } = canvas.getBoundingClientRect();

      canvas.width = width * ratio;
      canvas.height = height * ratio;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const loop = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;

      context.clearRect(0, 0, width, height);

      particlesRef.current.forEach((particle) => {
        particle.vy += particle.g;
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vx *= 0.99;
        particle.rot += particle.vr;
        particle.life -= 1;

        context.save();
        context.translate(particle.x, particle.y);
        context.rotate(particle.rot);
        context.fillStyle = particle.c;
        context.fillRect(
          -particle.s / 2,
          -particle.s / 2,
          particle.s,
          particle.s * 0.6
        );
        context.restore();
      });

      particlesRef.current = particlesRef.current.filter(
        (particle) => particle.life > 0 && particle.y < height + 40
      );
      animationRef.current = window.requestAnimationFrame(loop);
    };

    sizeCanvas();
    animationRef.current = window.requestAnimationFrame(loop);
    window.addEventListener("resize", sizeCanvas);

    return () => {
      window.removeEventListener("resize", sizeCanvas);
      timeoutRefs.current.forEach((timeoutId) =>
        window.clearTimeout(timeoutId)
      );

      if (animationRef.current !== null) {
        window.cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    canvasRef,
    celebrateWinner,
    celebrateFinale,
  };
};
